import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppContext } from "../../../context/AppContext";
import {
  requestAuthentication,
  useAuth,
} from "../../../context/AuthContext";
import { SECTIONS } from "../../../assets/MockData/mockData";
import { ReactComponent as ARROW_ICON } from "../../../assets/images/Frame (2).svg";
import { WALLET_VIEWS } from "../../Modals/CashierModals/financeWallet.helpers";
import { siteApi } from "../../../lib/api";
import { launchGameById, launchProviderLobby } from "../../../lib/gameLaunch";
import useSiteSettings from "../../../hooks/useSiteSettings";
import { openTelegramContact } from "../../../utils/telegramContact";
import {
  buildVendorFromFavorite,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
} from "../../../utils/favoriteGames";
import {
  getProfileRecentPlayHistory,
  getRecentPlayAccountKey,
  getRecentPlayHistory,
  mergeRecentPlayRows,
} from "../../../utils/recentPlayHistory";
import DropdownOptions from "./DropdownOptions";
import SideBarClosed from "./SideBarClosed";
import SidebarOption from "./SidebarOption";
import { StyledOpenedSidebar } from "./StyledSidebar";

const VENDOR_DROPDOWN_TYPES = ["live-casino", "slots"];
const SHORTCUT_DISPLAY_LIMIT = 8;
const RECENT_PLAY_UPDATED_EVENT = "crypto-recent-play-updated";
const SIDEBAR_WALLET_VIEWS = {
  "/account/deposits/ALL": WALLET_VIEWS.DEPOSIT,
  "/account/withdrawals/ALL": WALLET_VIEWS.WITHDRAW,
};

const getRows = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeKoreanTitle = (value = "") =>
  value
    .replace(/\bBTI\b/gi, "BTI")
    .replace(/\bBT1\b/gi, "BT1")
    .replace(/\bSABA\b/gi, "SABA")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "SA")
    .replace(/\s+/g, " ")
    .trim();

const getVendorTitle = (vendor) =>
  normalizeKoreanTitle(
    vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendor?.vendor_name ||
      "게임"
  );

const getVendorRoute = (gameType, vendor) => {
  const vendorName = encodeURIComponent(vendor?.vendor_name || "");
  if (gameType === "slots") return `/slots?vendor=${vendorName}`;
  return "#";
};

const ContinuePlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 3a9 9 0 1 0 8.38 5.72 1.05 1.05 0 0 0-1.96.76A6.9 6.9 0 1 1 11.3 5.13l-1.43 1.3a1.05 1.05 0 0 0 .7 1.83h5.15c.58 0 1.05-.47 1.05-1.05V2.06a1.05 1.05 0 0 0-1.76-.78L13.1 3.02C12.74 3 12.37 3 12 3Z"
    />
    <path fill="currentColor" d="M10.35 8.68v6.64c0 .7.77 1.13 1.36.76l5.2-3.32a.9.9 0 0 0 0-1.52l-5.2-3.32a.9.9 0 0 0-1.36.76Z" />
  </svg>
);

const FavoriteSidebarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="m12 3.35 2.56 5.18 5.72.83a1 1 0 0 1 .55 1.7l-4.14 4.04.98 5.7a1 1 0 0 1-1.45 1.05L12 19.63l-5.12 2.7a1 1 0 0 1-1.45-1.05l.98-5.7-4.14-4.04a1 1 0 0 1 .55-1.7l5.72-.83L11.1 3.35a1 1 0 0 1 1.8 0Z"
    />
  </svg>
);

const buildVendorFromRecent = (item = {}) => ({
  vendor_name: item.vendorName,
  vendor_name_kr: item.title,
  lobby_id: item.lobbyId,
  vendor_list: {
    lobby_id: item.lobbyId,
    thumbnails: item.image,
  },
  image: item.image,
  game_type: item.gameType,
});

const canLaunchShortcut = (item) =>
  Boolean(item?.href || item?.gameId || item?.lobbyId);

const createEmptyShortcutOption = (text, Icon) => ({
  icon: Icon,
  text,
  sidebarUrl: "#",
  disabled: true,
  isAction: true,
});

const SideBar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sections, setSections] = useState(SECTIONS);
  const [vendorsByType, setVendorsByType] = useState({});
  const [vendorLoading, setVendorLoading] = useState(true);
  const [launchingVendor, setLaunchingVendor] = useState("");
  const [shortcutDropdownOpen, setShortcutDropdownOpen] = useState(null);
  const [shortcutLaunchingKey, setShortcutLaunchingKey] = useState("");
  const [recentShortcutItems, setRecentShortcutItems] = useState([]);
  const [favoriteShortcutItems, setFavoriteShortcutItems] = useState([]);
  const [shouldRenderMobileSidebar, setShouldRenderMobileSidebar] = useState(false);
  const { settings } = useSiteSettings();
  const {
    isTabletScreen,
    selectedOption,
    isSidebarOpen,
    updateSelectedOption,
    updateSidebar,
    updateAttendanceModal,
    openCashierModal,
  } = useContext(AppContext);
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const hasAuthenticatedShortcuts = Boolean(user?.isAuthenticated && accountKey);

  useEffect(() => {
    let isMounted = true;

    const loadVendors = async () => {
      setVendorLoading(true);

      const entries = await Promise.all(
        [...VENDOR_DROPDOWN_TYPES, "live-sports"].map(async (gameType) => {
          const response = await siteApi.getGameVendors({
            game_type: gameType,
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          });

          return [gameType, response.success ? getRows(response) : []];
        })
      );

      if (!isMounted) return;
      setVendorsByType(Object.fromEntries(entries));
      setVendorLoading(false);
    };

    loadVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isTabletScreen) {
      setShouldRenderMobileSidebar(false);
      return undefined;
    }

    if (isSidebarOpen) {
      setShouldRenderMobileSidebar(true);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderMobileSidebar(false);
    }, 260);

    return () => window.clearTimeout(timeoutId);
  }, [isSidebarOpen, isTabletScreen]);

  useEffect(() => {
    if (!isTabletScreen || !isSidebarOpen || typeof window === "undefined") {
      return undefined;
    }

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousDocumentStyles = {
      overflow: documentElement.style.overflow,
      overscrollBehavior: documentElement.style.overscrollBehavior,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previousBodyStyles.overflow;
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.width = previousBodyStyles.width;
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      documentElement.style.overflow = previousDocumentStyles.overflow;
      documentElement.style.overscrollBehavior =
        previousDocumentStyles.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, [isSidebarOpen, isTabletScreen]);

  useEffect(() => {
    if (!hasAuthenticatedShortcuts) {
      setRecentShortcutItems([]);
      setFavoriteShortcutItems([]);
      setShortcutDropdownOpen(null);
    }
  }, [hasAuthenticatedShortcuts]);

  useEffect(() => {
    if (!hasAuthenticatedShortcuts) return undefined;

    const refreshRecentShortcuts = () => {
      setRecentShortcutItems(
        mergeRecentPlayRows(
          getRecentPlayHistory(accountKey),
          getProfileRecentPlayHistory(user)
        ).slice(0, SHORTCUT_DISPLAY_LIMIT)
      );
    };

    refreshRecentShortcuts();

    const handleRecentUpdate = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshRecentShortcuts();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshRecentShortcuts();
      }
    };

    window.addEventListener(RECENT_PLAY_UPDATED_EVENT, handleRecentUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(RECENT_PLAY_UPDATED_EVENT, handleRecentUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey, hasAuthenticatedShortcuts, user]);

  useEffect(() => {
    if (!hasAuthenticatedShortcuts) return undefined;

    const refreshFavoriteShortcuts = () => {
      setFavoriteShortcutItems(
        getFavoriteGames(accountKey).slice(0, SHORTCUT_DISPLAY_LIMIT)
      );
    };

    refreshFavoriteShortcuts();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavoriteShortcuts();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavoriteShortcuts();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey, hasAuthenticatedShortcuts]);

  const closeMobileSidebar = () => {
    if (isTabletScreen) updateSidebar(false);
  };

  const blockGuestMenu = (event, reason = "sidebar-menu") => {
    if (isAuthenticated) return false;

    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!user?.isLoading) requestAuthentication(reason);
    closeMobileSidebar();
    return true;
  };

  const getVendorDropdownOptions = (option) => {
    const gameType = option.dropdownSource;
    if (!gameType) return option.dropdownOptions || [];

    if (vendorLoading) {
      return [
        {
          icon: option.icon,
          text: "불러오는 중",
          sidebarUrl: option.sidebarUrl,
          disabled: true,
        },
      ];
    }

    const vendors = vendorsByType[gameType] || [];
    if (vendors.length === 0) {
      return [
        {
          icon: option.icon,
          text: "표시할 게임이 없습니다",
          sidebarUrl: option.sidebarUrl,
          disabled: true,
        },
      ];
    }

    return vendors.map((vendor) => {
      const isCasinoLobby = gameType === "live-casino";
      const launchKey = `${gameType}:${vendor?.vendor_name || vendor?.id || ""}`;

      return {
        icon: option.icon,
        text: getVendorTitle(vendor),
        sidebarUrl: getVendorRoute(gameType, vendor),
        vendorName: vendor?.vendor_name,
        vendor,
        launchMode: isCasinoLobby ? "casino-lobby" : null,
        isAction: isCasinoLobby,
        isLaunching: launchingVendor === launchKey,
      };
    });
  };

  const recentShortcutOptions = useMemo(() => {
    if (recentShortcutItems.length === 0) {
      return [
        createEmptyShortcutOption("최근 실행한 게임이 없습니다", ContinuePlayIcon),
      ];
    }

    return recentShortcutItems.map((item) => ({
      icon: ContinuePlayIcon,
      text: item.title,
      sidebarUrl: "#",
      shortcutKind: "recent",
      shortcutItem: item,
      isAction: true,
      disabled: item.disabled || !canLaunchShortcut(item),
      isLaunching: shortcutLaunchingKey === `recent:${item.key}`,
    }));
  }, [recentShortcutItems, shortcutLaunchingKey]);

  const favoriteShortcutOptions = useMemo(() => {
    if (favoriteShortcutItems.length === 0) {
      return [
        createEmptyShortcutOption("즐겨찾기한 게임이 없습니다", FavoriteSidebarIcon),
      ];
    }

    return favoriteShortcutItems.map((item) => ({
      icon: FavoriteSidebarIcon,
      text: item.title,
      sidebarUrl: item.href || "#",
      shortcutKind: "favorite",
      shortcutItem: item,
      isAction: true,
      disabled: !canLaunchShortcut(item),
      isLaunching: shortcutLaunchingKey === `favorite:${item.key}`,
    }));
  }, [favoriteShortcutItems, shortcutLaunchingKey]);

  const shortcutSection = useMemo(() => {
    if (!hasAuthenticatedShortcuts) return null;

    return {
      name: "내 게임",
      options: [
        {
          icon: ContinuePlayIcon,
          text: "계속 플레이",
          sidebarUrl: "#",
          shortcutType: "recent",
          hasDropdown: true,
          isOpenedDropdown: shortcutDropdownOpen === "recent",
          dropdownOptions: recentShortcutOptions,
          isScrollableDropdown: recentShortcutItems.length > 5,
        },
        {
          icon: FavoriteSidebarIcon,
          text: "즐겨찾기",
          sidebarUrl: "#",
          shortcutType: "favorite",
          hasDropdown: true,
          isOpenedDropdown: shortcutDropdownOpen === "favorite",
          dropdownOptions: favoriteShortcutOptions,
          isScrollableDropdown: favoriteShortcutItems.length > 5,
        },
      ],
    };
  }, [
    favoriteShortcutItems.length,
    favoriteShortcutOptions,
    hasAuthenticatedShortcuts,
    recentShortcutItems.length,
    recentShortcutOptions,
    shortcutDropdownOpen,
  ]);

  const hydratedSections = sections.map((section) => ({
    ...section,
    options: section.options.map((option) => ({
      ...option,
      dropdownOptions: option.dropdownSource
        ? getVendorDropdownOptions(option)
        : option.dropdownOptions,
      isLaunching: option.launchVendor === launchingVendor,
    })),
  }));

  const handleDropdownClick = (sectionIndex, optionIndex, sidebarUrl) => {
    setShortcutDropdownOpen(null);
    setSections((currentSections) =>
      currentSections.map((section, currentSectionIndex) => ({
        ...section,
        selectedOption:
          currentSectionIndex === sectionIndex &&
          section.options[optionIndex]?.selectedSubOption &&
          section.options[optionIndex]?.isOpenedDropdown
            ? sidebarUrl
            : section.selectedOption,
        options: section.options.map((option, currentOptionIndex) =>
          currentSectionIndex === sectionIndex && currentOptionIndex === optionIndex
            ? {
                ...option,
                isOpenedDropdown: !option.isOpenedDropdown,
              }
            : option
        ),
      }))
    );
  };

  const handleSidebarOptionClick = (sectionIndex, sidebarUrl) => {
    setSections((currentSections) =>
      currentSections.map((section, idx) => ({
        ...section,
        options: section.options.map((option) => ({
          ...option,
          selectedSubOption: null,
        })),
        selectedOption: idx === sectionIndex ? sidebarUrl : null,
      }))
    );
    updateSelectedOption(sidebarUrl);
    closeMobileSidebar();
  };

  const handleShortcutDropdownClick = (shortcutType) => {
    setSections((currentSections) =>
      currentSections.map((section) => ({
        ...section,
        options: section.options.map((option) => ({
          ...option,
          isOpenedDropdown: false,
        })),
      }))
    );
    setShortcutDropdownOpen((currentType) =>
      currentType === shortcutType ? null : shortcutType
    );
  };

  const launchSidebarShortcut = async (option, event) => {
    if (option?.disabled || !option?.shortcutItem) return;

    event?.preventDefault();

    const item = option.shortcutItem;
    const launchKey = `${option.shortcutKind}:${item.key}`;
    setShortcutLaunchingKey(launchKey);

    try {
      if (item.href) {
        updateSelectedOption(item.href);
        navigate(item.href);
        closeMobileSidebar();
        return;
      }

      if (item.kind === "provider") {
        const vendor =
          option.shortcutKind === "favorite"
            ? buildVendorFromFavorite(item)
            : buildVendorFromRecent(item);
        await launchProviderLobby(vendor, {
          label: item.title,
          gameType: item.gameType,
          meta: item.meta,
        });
        closeMobileSidebar();
        return;
      }

      await launchGameById(item.gameId, {
        label: item.title,
        recentPlay: item,
      });
      closeMobileSidebar();
    } finally {
      setShortcutLaunchingKey("");
    }
  };

  const handleDropdownOptionClick = (
    sectionIndex,
    mainOptionIndex,
    sidebarUrl,
    option,
    event
  ) => {
    if (blockGuestMenu(event, "sidebar-submenu")) return;
    if (option?.disabled) return;

    if (option?.shortcutKind) {
      launchSidebarShortcut(option, event);
      return;
    }

    if (option?.launchMode === "casino-lobby") {
      event?.preventDefault();
      const launchKey = `live-casino:${option.vendorName || option.vendor?.id || ""}`;
      setLaunchingVendor(launchKey);
      launchProviderLobby(option.vendor, { label: option.text }).finally(() => {
        setLaunchingVendor("");
      });
      closeMobileSidebar();
      return;
    }

    setSections((currentSections) =>
      currentSections.map((section, idx) => ({
        ...section,
        selectedOption: null,
        options: section.options.map((option, optIdx) => ({
          ...option,
          selectedSubOption:
            idx === sectionIndex && optIdx === mainOptionIndex
              ? sidebarUrl
              : null,
        })),
      }))
    );
    updateSelectedOption(sidebarUrl);
    closeMobileSidebar();
  };

  const findSportsVendor = (vendorKey) =>
    (vendorsByType["live-sports"] || []).find(
      (vendor) => vendor?.vendor_name?.toLowerCase() === vendorKey
    );

  // Match the homepage catalog: static shortcuts must not expose absent vendors.
  const isOptionVisible = (option) =>
    !option.launchVendor || Boolean(findSportsVendor(option.launchVendor));

  const launchSportsVendor = async (vendorKey) => {
    const vendor = findSportsVendor(vendorKey);
    const label = `${vendorKey.toUpperCase()} 스포츠`;

    setLaunchingVendor(vendorKey);
    launchProviderLobby(vendor, { label }).finally(() => setLaunchingVendor(""));
  };

  const handleTopOptionClick = (
    sectionIndex,
    optionIndex,
    option,
    event,
    { isCollapsed = false } = {}
  ) => {
    if (blockGuestMenu(event, "sidebar-menu")) return;

    if (option.shortcutType) {
      event.preventDefault();
      handleShortcutDropdownClick(option.shortcutType);
      return;
    }

    if (option.externalType === "telegram") {
      event.preventDefault();
      openTelegramContact(settings);
      closeMobileSidebar();
      return;
    }

    const walletView = SIDEBAR_WALLET_VIEWS[option.sidebarUrl];
    if (walletView) {
      event.preventDefault();
      updateSelectedOption(option.sidebarUrl);
      openCashierModal(walletView);
      closeMobileSidebar();
      return;
    }

    if (option.sidebarUrl === "/attendance-check") {
      event.preventDefault();
      updateSelectedOption(option.sidebarUrl);
      updateAttendanceModal(true);
      closeMobileSidebar();
      return;
    }

    if (option.launchVendor) {
      event.preventDefault();
      launchSportsVendor(option.launchVendor);
      closeMobileSidebar();
      return;
    }

    if (option.dropdownSource === "live-casino" && !isCollapsed) {
      event.preventDefault();
      handleDropdownClick(sectionIndex, optionIndex, option.sidebarUrl);
      return;
    }

    handleSidebarOptionClick(sectionIndex, option.sidebarUrl);
  };

  const handleTopDropdownClick = (sectionIndex, optionIndex, option) => {
    if (blockGuestMenu(undefined, "sidebar-menu")) return;

    if (option.shortcutType) {
      handleShortcutDropdownClick(option.shortcutType);
      return;
    }

    handleDropdownClick(sectionIndex, optionIndex, option.sidebarUrl);
  };

  const renderSidebarSection = (section, sectionIndex, isShortcutSection = false) => {
    const showCloseControl =
      isShortcutSection || (!shortcutSection && sectionIndex === 0);

    return (
      <div key={`${section.name}-${sectionIndex}`}>
        {showCloseControl ? (
          <div className="w-38 flex justify-between items-center cursor-pointer">
            <span className="other-text">{section.name}</span>
            <ARROW_ICON onClick={() => updateSidebar(false)} />
          </div>
        ) : (
          <span className="other-text">{section.name}</span>
        )}

        <div className="other-section">
          {section.options.map((option, optionIndex) => {
            if (!isAuthenticated && option.externalType === "telegram") return null;
            if (!isOptionVisible(option)) return null;

            return (
              <React.Fragment key={`${section.name}-${option.text}`}>
                <SidebarOption
                  onClick={(event) =>
                    handleTopOptionClick(sectionIndex, optionIndex, option, event)
                  }
                  onClickDropdown={() =>
                    handleTopDropdownClick(sectionIndex, optionIndex, option)
                  }
                  {...option}
                  isActive={option.sidebarUrl === selectedOption}
                  sidebarUrl={option.sidebarUrl}
                  isBiggerOption={true}
                  isAction={Boolean(
                    option.launchVendor || option.externalType || option.shortcutType
                  )}
                />

                {option.dropdownOptions && (
                  <DropdownOptions
                    options={option.dropdownOptions}
                    isOpen={Boolean(option.isOpenedDropdown)}
                    isScrollable={Boolean(
                      option.dropdownSource || option.isScrollableDropdown
                    )}
                    onSubOptionClick={(
                      subOptionIndex,
                      sidebarUrl,
                      dropdownOption,
                      event
                    ) =>
                      handleDropdownOptionClick(
                        sectionIndex,
                        optionIndex,
                        sidebarUrl,
                        dropdownOption,
                        event
                      )
                    }
                    activeSubOptionIndex={selectedOption}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  if (isTabletScreen && !isSidebarOpen && !shouldRenderMobileSidebar) return null;

  return (
    <StyledOpenedSidebar $isOpen={isSidebarOpen}>
      <button
        type="button"
        className="mobile-sidebar-backdrop"
        aria-label="사이드 메뉴 닫기"
        onClick={() => updateSidebar(false)}
      />

      <div className="sidebar-closed-panel" aria-hidden={isSidebarOpen}>
        <SideBarClosed
          toggleSideBar={updateSidebar}
          isAuthenticated={isAuthenticated}
          onGuestMenuClick={blockGuestMenu}
          isOptionVisible={isOptionVisible}
          onOptionSelect={(sectionIndex, optionIndex, option, event) =>
            handleTopOptionClick(sectionIndex, optionIndex, option, event, {
              isCollapsed: true,
            })
          }
        />
      </div>

      <div className="sidebar-open-panel" aria-hidden={!isSidebarOpen}>
        <div className="sidebar-content">
          {shortcutSection ? renderSidebarSection(shortcutSection, -1, true) : null}
          {hydratedSections.map((section, sectionIndex) =>
            renderSidebarSection(section, sectionIndex)
          )}
          <div className="divider" />
        </div>
      </div>
    </StyledOpenedSidebar>
  );
};

export default SideBar;
